import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Lock, Calculator, ShieldCheck, FileSpreadsheet, TrendingUp } from "lucide-react";
import { api, saveAuth } from "../lib/api";

export default function AccountingLogin() {
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      if (!["admin", "accountant"].includes(data.user.role)) {
        toast.error("This portal is for accountants only. Please use the regular login.");
        setLoading(false);
        return;
      }
      saveAuth(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      nav("/accounting");
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="fade-in min-h-[80vh] grid md:grid-cols-2">
      {/* LEFT PANEL — Branded, Navy */}
      <div className="hidden md:flex flex-col justify-between p-12 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg,#000080 0%,#1a1a6e 60%,#000080 100%)" }}>
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#FF9933]/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#138808]/20 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-xl bg-[#FF9933] flex items-center justify-center">
              <Calculator className="w-6 h-6 text-white" strokeWidth={1.7} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/70">AHCCT</div>
              <div className="serif text-xl font-semibold" style={{ color: "#ffffff" }}>Accounting Portal</div>
            </div>
          </div>

          <h1 className="serif text-4xl lg:text-5xl font-semibold leading-tight mb-5 !text-white" style={{ color: "#ffffff" }}>
            Books you can <em className="!text-[#FF9933] not-italic" style={{ color: "#FF9933" }}>trust</em>.<br />Audits you won't dread.
          </h1>
          <p className="text-white/70 max-w-md leading-relaxed">
            Double-entry ledger. Live Trial Balance. 80G summaries ready for ITR-7. Built for registered Indian NGOs.
          </p>

          <div className="space-y-3 mt-10 max-w-sm">
            <Feature icon={ShieldCheck} title="Role-based Access" desc="Accountants see ledgers; Admins see everything" />
            <Feature icon={FileSpreadsheet} title="Auto-Posted Donations" desc="Razorpay & PhonePe settlements reconciled automatically" />
            <Feature icon={TrendingUp} title="Live Reports" desc="Trial Balance · I&E · Balance Sheet · 80G Summary" />
          </div>
        </div>

        <div className="relative pt-10 border-t border-white/10 text-xs text-white/50">
          Adopt Helpless Children Charitable Trust · Reg 250501328202 · 80G & 12A Certified
        </div>
      </div>

      {/* RIGHT PANEL — Form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-[#FDFBF7]">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8 flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#FF9933] flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#737373]">AHCCT</div>
              <div className="serif text-lg font-semibold text-[#000080]">Accounting Portal</div>
            </div>
          </div>

          <h2 className="serif text-3xl font-semibold text-[#000080]">Accountant Sign-in</h2>
          <p className="text-sm text-[#737373] mt-1.5 mb-8">Secure access for AHCCT finance team members.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="accounting-login-form">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-[#737373] mb-1.5 block">Email</label>
              <Input required type="email" placeholder="you@adopthelplesschildren.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="acc-login-email" className="h-11" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-[#737373] mb-1.5 block flex justify-between">Password <Lock className="w-3 h-3 opacity-50" /></label>
              <Input required type="password" placeholder="••••••••" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="acc-login-password" className="h-11" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#FF9933] hover:bg-[#E68A2E] text-white h-12 rounded-full btn-glow" data-testid="acc-login-submit">
              {loading ? "Signing in..." : "Enter Accounting Portal →"}
            </Button>
          </form>

          <div className="mt-6 p-4 rounded-xl bg-white border border-[#eae5d9] text-xs text-[#737373]">
            <div className="font-bold text-[#000080] mb-1">Demo credentials (for review)</div>
            <div className="flex justify-between"><span>Accountant</span><span className="font-mono">accountant@adopthelplesschildren.com · Accounts@2026</span></div>
            <div className="flex justify-between mt-0.5"><span>Admin</span><span className="font-mono">admin@adopthelplesschildren.com · AHCCT@Admin2026</span></div>
          </div>

          <div className="mt-6 text-xs text-[#737373] text-center">
            Not an accountant? <Link to="/login" className="text-[#FF9933] font-semibold" data-testid="acc-login-to-regular">Regular Login</Link>
            <span className="mx-2 opacity-30">|</span>
            <Link to="/" className="text-[#000080] font-semibold" data-testid="acc-login-home">Back to site</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#FF9933]" strokeWidth={1.6} />
      </div>
      <div>
        <div className="font-bold text-white text-sm">{title}</div>
        <div className="text-xs text-white/60">{desc}</div>
      </div>
    </div>
  );
}
